/**
 * A preview-track version is stored as `{track}/{sha}` (e.g. `dev/pawel/abc1234`); a released one is semver.
 * The stored value is what the user reads everywhere — nothing composes a second identifier — but its slashes
 * mean any place that treats a version as a URL segment, a path or a filename has to encode it first.
 */
export function isPreviewVersion(version: string): boolean {
  return version.includes('/');
}

/**
 * Renders a version for display: semver keeps its `v` prefix, a preview version reads as stored.
 */
export function formatVersion(version?: string): string {
  if (!version) {
    return '';
  }
  return isPreviewVersion(version) ? version : `v${version}`;
}

/**
 * Reads a version back out of a URL path segment. React Router v5 hands params over raw, so a preview version
 * deep link arrives percent-encoded; a malformed escape is passed through rather than thrown.
 */
export function decodeVersionSegment(version?: string): string | undefined {
  if (!version) {
    return undefined;
  }
  try {
    return decodeURIComponent(version);
  } catch {
    return version;
  }
}

/**
 * Encodes a version for use as a nested URL path — each segment escaped, the separators kept. Asset keys are
 * stored under the same nesting, so a `%2F` here would point at a different object.
 */
export function versionPath(version: string): string {
  return version.split('/').map(encodeURIComponent).join('/');
}

/**
 * Flattens a version for use in a filename; a browser would otherwise read the slashes as a path and keep only
 * the last segment of the download name.
 */
export function versionFileSegment(version?: string): string {
  return (version ?? '').replace(/\//g, '_');
}
